import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { SERVER_NAME } from './config';
import { useAuth } from './auth/AuthContext';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 20,
    maxWidth: 900,
    margin: '0 auto',
    textAlign: 'left',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  keyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    marginBottom: 10,
    background: '#151922',
  },
  keyMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  keyName: {
    color: '#ffffff',
  },
  keyPrefix: {
    color: 'rgba(255,255,255,0.75)',
  },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  danger: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  apiGuideButton: {
    color: '#dce5ff',
    borderColor: 'rgba(77, 121, 255, 0.70)',
    background: 'rgba(77, 121, 255, 0.14)',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.95)',
      background: 'rgba(77, 121, 255, 0.26)',
    },
  },
  deleteIconButton: {
    color: '#ff8a8a',
    border: '1px solid rgba(255, 138, 138, 0.65)',
    background: 'rgba(255, 138, 138, 0.12)',
    '&:hover': {
      background: 'rgba(255, 138, 138, 0.22)',
      borderColor: 'rgba(255, 138, 138, 0.92)',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  dialogPaper: {
    background: '#12151c',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.12)',
    maxWidth: 920,
    width: '100%',
  },
  dialogActions: {
    padding: '16px 20px',
  },
  dialogContent: {
    padding: '26px 28px 18px',
  },
  dialogButton: {
    padding: '10px 18px',
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 10,
    textTransform: 'none',
  },
  createButton: {
    background: '#3f51b5',
    color: '#fff',
    '&:hover': {
      background: '#334296',
    },
    '&.Mui-disabled': {
      background: 'rgba(63,81,181,0.45)',
      color: 'rgba(255,255,255,0.7)',
    },
  },
  textFieldRoot: {
    '& .MuiOutlinedInput-root': {
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.25)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.45)',
    },
    '& .MuiInputLabel-outlined': {
      color: 'rgba(255,255,255,0.75)',
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255,255,255,0.65)',
    },
  },
  createdKeyField: {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 13,
      background: 'rgba(255,255,255,0.06)',
    },
  },
  curlField: {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 12,
      background: 'rgba(255,255,255,0.04)',
    },
  },
  tierPanel: {
    marginBottom: 16,
    padding: '15px',
    borderRadius: 10,
    background: 'rgba(77, 121, 255, 0.12)',
    border: '1px solid rgba(77, 121, 255, 0.45)',
  },
  tierGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginTop: 10,
  },
  tierCard: {
    padding: 12,
    borderRadius: 8,
    background: 'rgba(0, 0, 0, 0.22)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  tierLabel: {
    color: '#ffffff',
    margin: '0 0 6px 0',
    fontWeight: 'bold',
  },
  tierText: {
    color: '#a0a0a0',
    margin: 0,
    fontSize: '0.9em',
  },
}));

export default function ApiKeysPage({ openLogin, openSignup }) {
  const classes = useStyles();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const rateLimitTiers = [
    { label: 'Free', dailyLimit: '100 requests/day', interval: '5 seconds between requests' },
    { label: 'Premium', dailyLimit: '1000 requests/day', interval: '1 second between requests' },
    { label: 'Enterprise', dailyLimit: '100000 requests/day', interval: '10 milliseconds between requests' },
  ];

  const apiFetch = useCallback(async (path, options = {}) => {
    const url = `${SERVER_NAME}${path}`;
    const resp = await fetch(url, {
      credentials: 'include',
      ...options,
    });
    const text = await resp.text().catch(() => '');
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = null;
      }
    }
    if (!resp.ok) {
      const msg = data && typeof data === 'object' && data.message ? String(data.message) : '';
      const err = new Error(msg || `HTTP ${resp.status}`);
      err.status = resp.status;
      err.data = data;
      err.body = text;
      throw err;
    }
    return { resp, data, body: text };
  }, []);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [items, setItems] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState('');
  const createNameTrim = String(createName || '').trim();
  const createNameMissing = !createNameTrim;
  const maxReached = isAuthenticated && (items || []).length >= 5;

  const docsBase = String(SERVER_NAME || '').replace(/\/$/, '').replace(/\/api\/?$/, '');
  const storiesUrl = `${docsBase}/api/stories`;
  const createdCurl = createdKey
    ? `curl -sS '${storiesUrl}' \\
  -H 'Accept: application/json' \\
  -H 'x-api-key: ${createdKey}'`
    : '';

  const copyText = useCallback(async (text) => {
    const t = String(text || '');
    if (!t) return;
    try {
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(t);
        return;
      }
    } catch (e) {
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
    }
  }, []);

  const onCopyCreatedKey = useCallback(async () => {
    return copyText(createdKey);
  }, [copyText, createdKey]);

  const onCopyCurl = useCallback(async () => {
    return copyText(createdCurl);
  }, [copyText, createdCurl]);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setErr('');
    try {
      const out = await apiFetch('/keys');
      const rows = out && out.data && Array.isArray(out.data.items) ? out.data.items : [];
      setItems((rows || []).filter((r) => !(r && r.revokedAt)));
    } catch (e) {
      const status = e && e.status ? Number(e.status) : 0;
      if (status === 401 || status === 403) {
        setItems([]);
        setErr('');
      } else if (status === 503) {
        setErr('API keys are temporarily unavailable right now.');
      } else {
        setErr('Failed to load API keys');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiFetch]);

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setErr('');
      setLoading(false);
      return;
    }
    load();
  }, [load, isAuthenticated]);

  const requestAuth = useCallback(() => {
    try {
      if (typeof openLogin === 'function') {
        openLogin();
        return;
      }
    } catch (e) {
    }
    try {
      if (typeof openSignup === 'function') {
        openSignup();
        return;
      }
    } catch (e) {
    }
  }, [openLogin, openSignup]);

  const onCreate = async () => {
    setCreateLoading(true);
    setErr('');
    setCreatedKey('');
    try {
      const out = await apiFetch('/keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: createName }),
      });
      const data = out ? out.data : null;
      const k = data && typeof data === 'object' && data.key ? String(data.key) : '';
      if (!k) {
        const msg = data && typeof data === 'object' && data.message ? String(data.message) : '';
        setErr(msg || 'Server did not return an API key (check SERVER_NAME / auth)');
      } else {
        setCreatedKey(k);
      }
      await load();
    } catch (e) {
      const status = e && e.status ? Number(e.status) : 0;
      const msg = e && e.data && typeof e.data === 'object' && e.data.message ? String(e.data.message) : '';
      if (status === 401) {
        setErr('Login required to create an API key');
        requestAuth();
      } else {
        setErr(msg || (status ? `Failed to create API key (HTTP ${status})` : 'Failed to create API key'));
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const onRevoke = async (id) => {
    const keyId = String(id || '').trim();
    if (!keyId) return;
    setErr('');
    try {
      await apiFetch(`/keys/${encodeURIComponent(keyId)}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      setItems((prev) => (prev || []).filter((k) => String(k && k.id ? k.id : '') !== keyId));
    } catch (e) {
      setErr('Failed to revoke API key');
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <div>
          <Typography variant="h5">API Keys</Typography>
          <Typography variant="body2" color="textSecondary">
            Use `x-api-key` to access protected endpoints (starting with `/api/stories`).
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!isAuthenticated && (
            <>
              <Button variant="outlined" color="primary" onClick={requestAuth}>Log in</Button>
              <Button variant="outlined" color="primary" onClick={() => {
                try {
                  if (typeof openSignup === 'function') return openSignup();
                } catch (e) {
                }
                requestAuth();
              }}>Sign up</Button>
            </>
          )}
          <Button
            variant="outlined"
            color="primary"
            className={classes.apiGuideButton}
            onClick={() => navigate('/api-guide')}
          >
            API Guide
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!isAuthenticated) return requestAuth();
              if ((items || []).length >= 5) return;
              setCreateOpen(true);
              setCreateName('');
              setCreatedKey('');
            }}
            disabled={maxReached}
          >
            Create API Key
          </Button>
        </div>
      </div>

      <div className={classes.tierPanel}>
        <Typography variant="h6" style={{ color: '#50e3c2', marginTop: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>⏱</span>
          API Rate Limit Tiers
        </Typography>
        <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.75)' }}>
          Limits are enforced per API key.
        </Typography>
        <div className={classes.tierGrid}>
          {rateLimitTiers.map((tier) => (
            <div key={tier.label} className={classes.tierCard}>
              <p className={classes.tierLabel}>{tier.label}</p>
              <p className={classes.tierText}>{tier.dailyLimit}</p>
              <p className={classes.tierText} style={{ marginTop: 4 }}>{tier.interval}</p>
            </div>
          ))}
        </div>
      </div>

      {err && (
        <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: 12 }}>
          {err}
        </Typography>
      )}

      {!isAuthenticated ? (
        <Typography variant="body2" color="textSecondary">
          Log in to create and manage API keys.
        </Typography>
      ) : loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </div>
      ) : (
        <>
          {(items || []).map((k) => {
            const id = k && k.id ? String(k.id) : '';
            const name = k && k.name ? String(k.name) : '';
            const prefix = k && k.prefix ? String(k.prefix) : '';

            return (
              <div key={id} className={classes.keyRow}>
                <div className={classes.keyMeta}>
                  <Typography variant="subtitle1" className={classes.keyName}>{name || 'Unnamed key'}</Typography>
                  <Typography variant="body2" className={`${classes.mono} ${classes.keyPrefix}`}>
                    {prefix ? `${prefix}…` : ''}
                  </Typography>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <Tooltip title="Revoke" placement="top">
                    <span>
                      <IconButton
                        aria-label="Revoke"
                        onClick={() => onRevoke(id)}
                        style={{
                          borderRadius: 10,
                        }}
                        className={`${classes.danger} ${classes.deleteIconButton}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            );
          })}

          {(!items || items.length === 0) && (
            <Typography variant="body2" color="textSecondary">No API keys yet.</Typography>
          )}

          {maxReached && (
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.75)', marginTop: 16 }}>
              Maximum number of API keys reached. Revoke one to create a new one.
            </Typography>
          )}
        </>
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}>
            <Typography variant="h6" style={{ color: '#fff', fontWeight: 700 }}>Create API Key</Typography>
            <IconButton onClick={() => setCreateOpen(false)} size="small" style={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" style={{ marginBottom: 18, color: 'rgba(255,255,255,0.75)' }}>
            The new key will be shown only once. Copy it.
          </Typography>

          {err && (
            <Typography variant="body2" style={{ color: '#ff6b6b', marginBottom: 18 }}>
              {err}
            </Typography>
          )}

          <TextField
            label="Name"
            required
            fullWidth
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            variant="outlined"
            size="small"
            disabled={!!createdKey}
            error={!!(!createdKey && createNameMissing)}
            helperText={!createdKey && createNameMissing ? 'Name is required' : ' '}
            style={{ marginTop: 10, marginBottom: 22 }}
            className={classes.textFieldRoot}
          />

          {createdKey && (
            <>
              <TextField
                label="Your new API key"
                fullWidth
                value={createdKey}
                variant="outlined"
                size="small"
                multiline
                minRows={3}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton onClick={onCopyCreatedKey} size="small" style={{ color: 'rgba(255,255,255,0.85)' }}>
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                className={`${classes.textFieldRoot} ${classes.createdKeyField}`}
                style={{ marginBottom: 22 }}
              />
              <TextField
                label="Example (curl)"
                fullWidth
                value={createdCurl}
                variant="outlined"
                size="small"
                multiline
                minRows={6}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton onClick={onCopyCurl} size="small" style={{ color: 'rgba(255,255,255,0.85)' }}>
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                className={`${classes.textFieldRoot} ${classes.curlField}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setCreateOpen(false)} color="default" className={classes.dialogButton} style={{ color: 'rgba(255,255,255,0.85)' }}>Close</Button>
          {!createdKey && (
            <Button
              onClick={onCreate}
              variant="contained"
              color="primary"
              disabled={createLoading || createNameMissing}
              className={`${classes.dialogButton} ${classes.createButton}`}
            >
              {createLoading ? 'Creating…' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
